import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

export type MobileTab = 'today' | 'leads' | 'deals' | 'tasks' | 'more';

const TAB_ICON: Record<MobileTab, ReactNode> = {
  today: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ),
  leads: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  ),
  deals: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path d="M3 13h18" />
    </svg>
  ),
  tasks: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="m8 12 3 3 5-6" />
    </svg>
  ),
  more: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
};

// 'Opportunities' wraps to 2 lines in this 5-column bar's ~75px-wide column
// (no truncate/ellipsis is applied to this label) — 'Pipeline' is the same
// synonym the dashboard already uses for this page ("Sales pipeline", "Open
// pipeline") and fits on one line alongside the other 4 short tab labels.
const TAB_LABEL: Record<MobileTab, string> = { today: 'Today', leads: 'Leads', deals: 'Pipeline', tasks: 'Tasks', more: 'More' };
const TAB_HREF: Record<MobileTab, string> = { today: '/m', leads: '/m/leads', deals: '/deals', tasks: '/tasks', more: '/m/more' };

/** design/orion-crm-design/rendered-html/Mobile*.html's shared bottom nav —
 * present on all 3 real mobile screens. Only Today/Leads/Lead-detail/Quick-log
 * have a mobile-shaped screen in this handoff (docs/architecture/08-v1-scope.md),
 * but Deals and Tasks are real, fully-working desktop pages with real data —
 * routing them to a "not built yet" dead end when a working (if not
 * mobile-optimised) page exists one tap away was worse than the honest
 * placeholder it replaced. Same escape-hatch pattern as MobileLeadDetailPage's
 * Edit/Convert/More-actions buttons. "More" has no desktop equivalent to fall
 * back to, so it keeps its own page. */
export function MobileBottomNav({ active, tasksBadge }: { active: MobileTab; tasksBadge?: number }) {
  const tabs: MobileTab[] = ['today', 'leads', 'deals', 'tasks', 'more'];
  return (
    <nav aria-label="Primary" className="grid h-16 shrink-0 grid-cols-5 border-t border-border bg-surface px-1">
      {tabs.map((tab) => {
        const isActive = tab === active;
        return (
          <Link
            key={tab}
            to={TAB_HREF[tab]}
            aria-current={isActive ? 'page' : undefined}
            aria-label={tab === 'tasks' && tasksBadge ? `Tasks, ${tasksBadge} due` : undefined}
            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] no-underline ${isActive ? 'font-semibold text-primary' : 'font-medium text-text-secondary'}`}
          >
            <span className={`relative flex h-7 w-13 items-center justify-center ${isActive ? 'rounded-full bg-primary-soft' : ''}`}>
              {TAB_ICON[tab]}
              {tab === 'tasks' && !!tasksBadge && (
                <span className="absolute right-1.5 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-warning px-1 text-[10px] font-bold text-night">{tasksBadge}</span>
              )}
            </span>
            {TAB_LABEL[tab]}
          </Link>
        );
      })}
    </nav>
  );
}

const AVATAR_COLORS = ['#0F6B63', '#B42318', '#3A3F8F', '#8A4B06', '#5B34B0', '#1E7A4C'];

/** Deterministic per-person avatar colour, the same hashing approach as
 * AuditLogPage's AvatarDot — real names, not a fixed palette assigned by
 * hand. */
export function avatarColor(name: string): string {
  const hue = Math.abs([...name].reduce((h, c) => h * 31 + c.charCodeAt(0), 0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[hue]!;
}

export function initialsOf(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Status pill colours derived from the real per-status `color` hex (Admin
 * ▸ Leads — each status carries its own admin-set colour, independent of
 * `category`: e.g. "New"/"Contacted"/"Nurturing" all share category `open`
 * but are visually distinct in design/orion-crm-design/rendered-html/
 * MobileLeads.html). `color-mix` derives a soft tint background from that
 * one stored value instead of a hardcoded per-name/category lookup table. */
export function statusPillStyle(color: string): { bg: string; fg: string } {
  return { bg: `color-mix(in srgb, ${color} 14%, white)`, fg: color };
}
